<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useIntervalFn,
  useNow,
  useFullscreen,
  useDocumentVisibility,
  useDebounceFn,
  watchDebounced,
} from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import liveConversationsAPI from 'dashboard/api/liveConversations';
import summaryReportsAPI from 'dashboard/api/summaryReports';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import LiveColumn from '../components/LiveColumn.vue';

// Websocket events trigger the refresh; polling is only a safety net
const REFRESH_INTERVAL_MS = 60000;
const EVENT_REFRESH_DEBOUNCE_MS = 1000;
// TME/TMA come from a heavier report query, so they refresh on their own slower cycle
const STATS_REFRESH_INTERVAL_MS = 300000;

const { t, locale } = useI18n();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');

const pageRef = ref(null);
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(pageRef);
const visibility = useDocumentVisibility();
const nowMs = useNow({ interval: 30000 });
const now = computed(() => Math.floor(nowMs.value.getTime() / 1000));

const conversations = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const lastUpdatedAt = ref(null);

const agentStats = ref({});

const search = ref('');
const debouncedSearch = ref('');
const agentFilter = ref('all');
const inboxFilter = ref('all');
const onlyWaiting = ref(false);

watchDebounced(
  search,
  value => {
    debouncedSearch.value = value;
  },
  { debounce: 250 }
);

const fetchConversations = async () => {
  try {
    const { data } = await liveConversationsAPI.get();
    conversations.value = data.payload;
    lastUpdatedAt.value = Date.now();
    hasError.value = false;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const fetchAgentStats = async () => {
  try {
    const since = Math.floor(new Date().setHours(0, 0, 0, 0) / 1000);
    const until = Math.floor(Date.now() / 1000);
    const { data } = await summaryReportsAPI.getAgentReports({
      since,
      until,
      businessHours: false,
    });
    agentStats.value = Object.fromEntries(
      data.map(item => [
        item.id,
        {
          tme: item.avg_first_response_time,
          tma: item.avg_resolution_time,
        },
      ])
    );
  } catch {
    agentStats.value = {};
  }
};

// Pause polling while the tab is hidden; refresh right away when it returns
const { pause, resume } = useIntervalFn(
  fetchConversations,
  REFRESH_INTERVAL_MS,
  {
    immediateCallback: false,
  }
);
const { pause: pauseStats, resume: resumeStats } = useIntervalFn(
  fetchAgentStats,
  STATS_REFRESH_INTERVAL_MS,
  { immediateCallback: false }
);
watchDebounced(
  visibility,
  value => {
    if (value === 'visible') {
      fetchConversations();
      fetchAgentStats();
      resume();
      resumeStats();
    } else {
      pause();
      pauseStats();
    }
  },
  { debounce: 100 }
);
const refreshOnEvent = useDebounceFn(
  fetchConversations,
  EVENT_REFRESH_DEBOUNCE_MS,
  { maxWait: 3000 }
);
onMounted(() => {
  fetchConversations();
  fetchAgentStats();
  emitter.on(BUS_EVENTS.LIVE_CONVERSATIONS_CHANGED, refreshOnEvent);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchConversations);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.LIVE_CONVERSATIONS_CHANGED, refreshOnEvent);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchConversations);
});

const labelColors = computed(() =>
  Object.fromEntries(labels.value.map(label => [label.title, label.color]))
);

const filteredConversations = computed(() => {
  const term = debouncedSearch.value.trim().toLowerCase();
  return conversations.value.filter(conversation => {
    if (
      inboxFilter.value !== 'all' &&
      conversation.inbox_id !== inboxFilter.value
    )
      return false;
    if (onlyWaiting.value && !conversation.waiting_since) return false;
    if (!term) return true;
    return (
      (conversation.contact_name || '').toLowerCase().includes(term) ||
      String(conversation.id).includes(term.replace('#', ''))
    );
  });
});

const columns = computed(() => {
  const byAssignee = new Map();
  filteredConversations.value.forEach(conversation => {
    const list = byAssignee.get(conversation.assignee_id) || [];
    list.push(conversation);
    byAssignee.set(conversation.assignee_id, list);
  });
  const agentColumns = agents.value
    .filter(
      agent => agentFilter.value === 'all' || agent.id === agentFilter.value
    )
    .map(agent => ({
      key: agent.id,
      title: agent.name,
      agent,
      stats: agentStats.value[agent.id],
      conversations: byAssignee.get(agent.id) || [],
    }))
    .sort(
      (a, b) =>
        b.conversations.length - a.conversations.length ||
        a.title.localeCompare(b.title)
    );

  if (agentFilter.value !== 'all') return agentColumns;
  return [
    {
      key: 'unassigned',
      title: t('LIVE_VIEW.UNASSIGNED'),
      agent: null,
      conversations: byAssignee.get(null) || [],
    },
    ...agentColumns,
  ];
});

const totalCount = computed(() => filteredConversations.value.length);

const lastUpdatedLabel = computed(() => {
  if (!lastUpdatedAt.value) return '';
  return new Date(lastUpdatedAt.value).toLocaleTimeString(
    locale.value.replace('_', '-'),
    {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }
  );
});

const inputClass =
  'h-10 px-3 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand';
// Global form styles force full width and a bottom margin on selects
const selectClass = `${inputClass} !w-auto !mb-0 !h-10 !bg-origin-border ltr:!bg-[right_0.75rem_center] rtl:!bg-[left_0.75rem_center] ltr:!pr-9 rtl:!pl-9`;
</script>

<template>
  <div
    ref="pageRef"
    class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1"
  >
    <div class="flex flex-col gap-4 px-6 py-6 mx-auto w-full max-w-[120rem]">
      <header class="flex flex-wrap items-center gap-3">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('LIVE_VIEW.TITLE') }}
        </h1>
        <span
          class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
          :class="{ 'bg-n-ruby-3 text-n-ruby-11': hasError }"
        >
          <span
            class="rounded-full size-1.5"
            :class="hasError ? 'bg-n-ruby-9' : 'bg-n-teal-9 animate-pulse'"
          />
          {{ hasError ? t('LIVE_VIEW.ERROR') : t('LIVE_VIEW.LIVE') }}
        </span>
        <span class="text-sm tabular-nums text-n-slate-11">
          {{ t('LIVE_VIEW.TOTAL', { count: totalCount }) }}
        </span>
        <span v-if="lastUpdatedLabel" class="text-xs text-n-slate-10">
          {{ t('LIVE_VIEW.UPDATED_AT', { time: lastUpdatedLabel }) }}
        </span>

        <div class="flex flex-wrap items-center gap-3 ltr:ml-auto rtl:mr-auto">
          <div class="relative">
            <Icon
              icon="i-lucide-search"
              class="absolute size-4 -translate-y-1/2 top-1/2 ltr:left-3 rtl:right-3 text-n-slate-10"
            />
            <input
              v-model="search"
              type="search"
              :placeholder="t('LIVE_VIEW.SEARCH')"
              class="reset-base ltr:pl-9 rtl:pr-9 w-72"
              :class="[inputClass]"
            />
          </div>
          <select v-model="agentFilter" :class="selectClass">
            <option value="all">{{ t('LIVE_VIEW.ALL_AGENTS') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
          <select v-model="inboxFilter" :class="selectClass">
            <option value="all">{{ t('LIVE_VIEW.ALL_INBOXES') }}</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
          <Button
            :label="t('LIVE_VIEW.ONLY_WAITING')"
            :variant="onlyWaiting ? 'solid' : 'faded'"
            color="slate"
            icon="i-lucide-hourglass"
            @click="onlyWaiting = !onlyWaiting"
          />
          <Button
            icon="i-lucide-refresh-cw"
            variant="ghost"
            color="slate"
            :is-loading="isLoading"
            :title="t('LIVE_VIEW.REFRESH')"
            @click="fetchConversations"
          />
          <Button
            :icon="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
            variant="ghost"
            color="slate"
            :title="t('LIVE_VIEW.FULLSCREEN')"
            @click="toggleFullscreen"
          />
        </div>
      </header>

      <div
        class="grid gap-4 grid-cols-[repeat(auto-fill,minmax(20rem,1fr))] items-start"
      >
        <LiveColumn
          v-for="column in columns"
          :key="column.key"
          :title="column.title"
          :agent="column.agent"
          :stats="column.stats"
          :conversations="column.conversations"
          :now="now"
          :label-colors="labelColors"
        />
      </div>
    </div>
  </div>
</template>
